<template>
  <div>
    <button @click="$emit('exit')" class="text-sm text-gray-500 hover:text-gray-700 mb-4">&larr; All games</button>

    <div v-if="loading" class="text-center py-16 text-gray-500">Loading today's challenge…</div>
    <div v-else-if="error" class="text-center py-16 text-red-500">{{ error }}</div>

    <!-- Already done today -->
    <div v-else-if="doneToday && !justFinished" class="bg-white rounded-3xl border border-gray-200 shadow-sm p-8 text-center">
      <div class="text-4xl mb-2">✅</div>
      <h2 class="text-2xl font-bold text-gray-900">Today's challenge is done!</h2>
      <p class="mt-2 text-gray-600">Come back tomorrow for a fresh one.</p>
      <button @click="$emit('exit')" class="mt-6 bg-emerald-600 text-white font-medium px-6 py-3 rounded-xl hover:bg-emerald-700">Back to games</button>
    </div>

    <!-- Results -->
    <div v-else-if="finished" class="bg-white rounded-3xl border border-gray-200 shadow-sm p-8 text-center">
      <div class="text-4xl mb-2">🏆</div>
      <h2 class="text-2xl font-bold text-gray-900">{{ correctCount }} / {{ exercises.length }} correct</h2>
      <p class="mt-1 text-emerald-600 font-medium">+{{ xpEarned }} XP<span v-if="xpEarned === 0" class="text-gray-400"> (daily cap reached)</span></p>
      <button @click="$emit('exit')" class="mt-6 bg-emerald-600 text-white font-medium px-6 py-3 rounded-xl hover:bg-emerald-700">Back to games</button>
    </div>

    <!-- Playing -->
    <div v-else-if="current">
      <div class="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 sm:p-8">
        <div class="flex items-center justify-between text-xs font-medium text-gray-400">
          <span>🗓️ Daily Challenge</span>
          <span>{{ index + 1 }} / {{ exercises.length }}</span>
        </div>
        <div class="mt-2 mb-6 h-1.5 w-full rounded-full bg-gray-100">
          <div class="h-1.5 rounded-full bg-emerald-500 transition-all duration-300" :style="{ width: `${(index / exercises.length) * 100}%` }"></div>
        </div>

        <p class="text-sm text-gray-500 mb-2">{{ current.instruction }}</p>
        <div class="flex items-center gap-3 mb-5">
          <h3 class="text-2xl font-bold text-gray-900">{{ current.prompt }}</h3>
          <AudioButton v-if="current.audio_text" :src="current.audio_url" :text="current.audio_text" />
        </div>

        <!-- Option-based -->
        <div v-if="current.options && current.options.length" class="space-y-2.5">
          <button
            v-for="(opt, i) in current.options" :key="i"
            @click="!answered && choose(opt)"
            :disabled="answered"
            class="w-full flex items-center justify-between gap-3 text-left px-4 py-3.5 rounded-xl border-2 font-medium transition-all"
            :class="optionClass(opt)"
          >
            <span>{{ opt.text }}</span>
            <span v-if="answered && opt.is_correct" class="text-emerald-600">&#10003;</span>
            <span v-else-if="answered && opt === selected" class="text-red-500">&#10007;</span>
          </button>
        </div>

        <!-- Text input -->
        <div v-else>
          <VietnameseInput
            v-model="typed" :disabled="answered" @enter="submitTyped"
            class="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            placeholder="Type your answer…"
          />
          <button v-if="!answered" @click="submitTyped" class="mt-3 bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl shadow-sm hover:bg-emerald-700 transition-colors">Check</button>
        </div>

        <div v-if="answered" class="mt-5 p-4 rounded-xl" :class="lastCorrect ? 'bg-emerald-50' : 'bg-red-50'">
          <p :class="lastCorrect ? 'text-emerald-700' : 'text-red-700'" class="font-semibold">
            {{ lastCorrect ? 'Correct!' : `Answer: ${current.correct_answer}` }}
          </p>
          <p v-if="lastCorrect && toneMismatch" class="text-sm text-amber-700 mt-1">
            Watch your tones &rarr; <span class="font-medium">{{ current.correct_answer }}</span>
          </p>
          <p v-if="current.explanation" class="text-sm text-gray-600 mt-1">{{ current.explanation }}</p>
          <button @click="next" class="mt-3 bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl shadow-sm hover:bg-emerald-700 transition-colors">
            {{ index + 1 < exercises.length ? 'Next' : 'Finish' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { apiGet, apiPost } from '../../api/client'
import AudioButton from '../shared/AudioButton.vue'
import VietnameseInput from '../shared/VietnameseInput.vue'
import { checkVietnameseAnswer } from '../../utils/similarity'

defineEmits(['exit'])

const loading = ref(true)
const error = ref(null)
const doneToday = ref(false)
const exercises = ref([])
const index = ref(0)
const answered = ref(false)
const lastCorrect = ref(false)
const toneMismatch = ref(false)
const selected = ref(null)
const typed = ref('')
const strictTones = (() => {
  try { return localStorage.getItem('speaksaigon_strict_tones') !== 'false' } catch (e) { return true }
})()
const correctCount = ref(0)
const finished = ref(false)
const justFinished = ref(false)
const xpEarned = ref(0)

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
const current = computed(() => exercises.value[index.value] || null)

function optionClass(opt) {
  if (!answered.value) return 'border-gray-200 bg-white text-gray-800 hover:border-emerald-400'
  if (opt.is_correct) return 'border-emerald-500 bg-emerald-50 text-emerald-700'
  if (opt === selected.value) return 'border-red-500 bg-red-50 text-red-700'
  return 'border-gray-200 bg-white text-gray-400 opacity-60'
}

function choose(opt) {
  selected.value = opt
  lastCorrect.value = !!opt.is_correct
  if (lastCorrect.value) correctCount.value += 1
  answered.value = true
}

function submitTyped() {
  if (answered.value || !typed.value.trim()) return
  const result = checkVietnameseAnswer(
    typed.value,
    current.value.correct_answer,
    current.value.acceptable_answers || [],
    { strict: strictTones },
  )
  lastCorrect.value = result.isCorrect
  toneMismatch.value = result.toneMismatch
  if (lastCorrect.value) correctCount.value += 1
  answered.value = true
}

function next() {
  if (index.value + 1 < exercises.value.length) {
    index.value += 1
    answered.value = false
    selected.value = null
    typed.value = ''
    toneMismatch.value = false
  } else {
    finish()
  }
}

async function finish() {
  finished.value = true
  justFinished.value = true
  try {
    const res = await apiPost('/api/v1/games/record', {
      game: 'daily_challenge', score: correctCount.value,
      correct: correctCount.value, total: exercises.value.length, timezone: tz,
    })
    xpEarned.value = res?.xp_earned ?? 0
  } catch (e) { /* results still shown */ }
}

onMounted(async () => {
  try {
    const data = await apiGet(`/api/v1/games/daily_challenge?timezone=${encodeURIComponent(tz)}`)
    doneToday.value = data.done_today
    exercises.value = data.exercises || []
    if (!doneToday.value && exercises.value.length === 0) {
      error.value = 'Practice a few lessons first to unlock the daily challenge!'
    }
  } catch (e) {
    error.value = 'Could not load the daily challenge.'
  } finally {
    loading.value = false
  }
})
</script>
